/**
 * What a microphone-permission reload may keep (outlook-orachat-uplift s9, C47).
 *
 * Only the unsent composer text and whether dictation should resume, for
 * five minutes, under their own keys. The conversation comes back from the
 * server. Every storage access is inside try/catch: a missing or throwing
 * store answers "nothing kept" and never throws. Nothing else is written.
 */

const UNSENT_KEY = "ora.addin.unsentText";
const RESUME_KEY = "ora.addin.resumeIntent";
const TTL_MS = 5 * 60 * 1000;
const VERSION = 1;

export interface ReloadState {
  unsentText: string | null;
  resumeDictation: boolean;
}

const EMPTY: ReloadState = { unsentText: null, resumeDictation: false };

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readEntry(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object") return null;
  const entry = parsed as Record<string, unknown>;
  if (entry.v !== VERSION) return null;
  if (typeof entry.at !== "number" || Date.now() - entry.at > TTL_MS) return null;
  return entry;
}

/** Keep the composer text and the resume intent. Never throws. */
export function saveReloadState(state: { unsentText: string; resumeDictation: boolean }): void {
  const store = storage();
  if (!store) return;
  const at = Date.now();
  try {
    store.setItem(UNSENT_KEY, JSON.stringify({ v: VERSION, at, unsentText: state.unsentText }));
  } catch {
    // A full or blocked store keeps nothing.
  }
  try {
    store.setItem(
      RESUME_KEY,
      JSON.stringify({ v: VERSION, at, resumeDictation: state.resumeDictation === true }),
    );
  } catch {
    // A full or blocked store keeps nothing.
  }
}

/**
 * Read both keys, drop anything stale or of another version, then remove
 * both. Never throws.
 */
export function takeReloadState(): ReloadState {
  const store = storage();
  if (!store) return { ...EMPTY };
  let unsentText: string | null = null;
  let resumeDictation = false;
  try {
    const entry = readEntry(store.getItem(UNSENT_KEY));
    if (entry && typeof entry.unsentText === "string") unsentText = entry.unsentText;
  } catch {
    unsentText = null;
  }
  try {
    const entry = readEntry(store.getItem(RESUME_KEY));
    resumeDictation = entry?.resumeDictation === true;
  } catch {
    resumeDictation = false;
  }
  try {
    store.removeItem(UNSENT_KEY);
  } catch {
    // Already gone, or the store refused.
  }
  try {
    store.removeItem(RESUME_KEY);
  } catch {
    // Already gone, or the store refused.
  }
  return { unsentText, resumeDictation };
}

/** Whether a reload asked to reopen the Assistant. Does not consume the keys. */
export function hasResumeIntent(): boolean {
  try {
    const store = storage();
    if (!store) return false;
    const entry = readEntry(store.getItem(RESUME_KEY));
    return entry?.resumeDictation === true;
  } catch {
    return false;
  }
}
